import React, { useState } from 'react';
import {
  X, Pencil, Tag, Link2, Unlink, Globe2, Star, Trash2,
  ChevronLeft, Check, Loader, AlertCircle, Lock
} from 'lucide-react';

const Row = ({ icon: Icon, label, hint, onClick, disabled, danger, active }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`w-full flex items-center gap-3 px-4 min-h-[52px] text-left transition-colors disabled:opacity-50 ${
      danger ? 'text-red-600 active:bg-red-50' : 'text-gray-900 active:bg-gray-50'
    }`}
  >
    <Icon size={20} className={active ? 'text-green-600' : danger ? 'text-red-500' : 'text-gray-500'} />
    <span className="flex-1">
      <span className="block text-sm font-medium">{label}</span>
      {hint && <span className="block text-xs text-gray-500">{hint}</span>}
    </span>
  </button>
);

/**
 * Bottom sheet with every action for one photo tile.
 * Presentational: the parent owns the data and the API calls.
 */
const PhotoActionSheet = ({
  photo, photos, photoTypes, allowedTypes, busy, error,
  onClose, onSave, onToggleApproval, onToggleFeatured, onDelete,
}) => {
  const [view, setView] = useState('menu'); // 'menu' | 'label' | 'type' | 'match'
  const [captionDraft, setCaptionDraft] = useState(photo.caption || '');

  const isBefore = photo.type === 'Before';
  const isAfter = photo.type === 'After';
  const canWebsite = isBefore || isAfter;
  const befores = photos.filter(p => p.type === 'Before');
  const isPaired = isAfter && !!photo.pairedWithPhotoId;

  const saveAndReturn = async (payload, failMsg) => {
    const ok = await onSave(payload, failMsg);
    if (ok) setView('menu');
  };

  const title = { menu: null, label: 'Edit label', type: 'Change type', match: 'Match with a Before' }[view];

  return (
    <div className="fixed inset-0 z-[60]" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50" />
      <div
        className="absolute bottom-0 inset-x-0 sm:max-w-md sm:mx-auto bg-white rounded-t-2xl max-h-[85vh] overflow-y-auto"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 p-4 border-b border-gray-100">
          {view !== 'menu' ? (
            <button onClick={() => setView('menu')} className="p-1 -ml-1 text-gray-500" aria-label="Back">
              <ChevronLeft size={22} />
            </button>
          ) : (
            <img
              src={photo.thumbnailUrl || photo.url}
              alt=""
              className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
              style={{ objectPosition: 'center 20%' }}
            />
          )}
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-900 truncate">{title || photo.caption || 'No label'}</p>
            {view === 'menu' && (
              <p className="text-xs text-gray-500">
                {photo.type}{isPaired ? ' • matched' : ''}{photo.isWebsiteApproved ? ' • on website' : ''}
              </p>
            )}
          </div>
          <button onClick={onClose} className="p-2 text-gray-400" aria-label="Close">
            <X size={20} />
          </button>
        </div>

        {error && (
          <div className="mx-4 mt-3 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
            <AlertCircle size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* MENU */}
        {view === 'menu' && (
          <div className="py-1">
            <Row icon={Pencil} label="Edit label" hint={photo.caption || 'No label yet'} onClick={() => { setCaptionDraft(photo.caption || ''); setView('label'); }} />
            <Row icon={Tag} label="Change type" hint={photo.type} onClick={() => setView('type')} />
            {isAfter && befores.length > 0 && (
              <Row icon={Link2} label={isPaired ? 'Change matched Before' : 'Match with a Before'} onClick={() => setView('match')} />
            )}
            {isPaired && (
              <Row icon={Unlink} label="Unmatch" hint="Removes it from the website too" disabled={busy}
                onClick={() => onSave({ clearPairing: true }, 'Failed to unmatch photo')} />
            )}
            {canWebsite && (
              <Row
                icon={Globe2}
                active={photo.isWebsiteApproved}
                label={photo.isWebsiteApproved ? 'Remove from website' : 'Approve for website'}
                hint="Shows on the site once the Before and After are both approved"
                disabled={busy}
                onClick={onToggleApproval}
              />
            )}
            {canWebsite && photo.isWebsiteApproved && (
              <Row
                icon={Star}
                active={photo.isFeatured}
                label={photo.isFeatured ? 'Remove from homepage' : 'Feature on homepage'}
                hint="Applies to the whole pair"
                disabled={busy}
                onClick={onToggleFeatured}
              />
            )}
            <div className="border-t border-gray-100 mt-1">
              <Row icon={Trash2} danger label="Delete photo" onClick={onDelete} />
            </div>
          </div>
        )}

        {/* LABEL */}
        {view === 'label' && (
          <div className="p-4 space-y-3">
            <input
              type="text"
              value={captionDraft}
              onChange={(e) => setCaptionDraft(e.target.value)}
              maxLength={500}
              autoFocus
              placeholder="e.g., Front porch, Driveway"
              className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <button
              onClick={() => saveAndReturn({ caption: captionDraft.trim() }, 'Failed to save label')}
              disabled={busy}
              className="w-full min-h-[48px] rounded-lg bg-blue-600 text-white font-medium disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {busy ? <Loader size={16} className="animate-spin" /> : <Check size={16} />}
              Save label
            </button>
          </div>
        )}

        {/* TYPE */}
        {view === 'type' && (
          <div className="p-4 space-y-2">
            {(isBefore || isAfter) && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
                Changing the type unmatches this photo and removes it from the website.
              </p>
            )}
            {Object.entries(photoTypes).map(([type, cfg]) => {
              const allowed = !allowedTypes || allowedTypes.includes(type);
              const current = photo.type === type;
              return (
                <button
                  key={type}
                  disabled={!allowed || current || busy}
                  onClick={() => saveAndReturn({ type }, 'Failed to change type')}
                  className={`w-full flex items-center gap-3 px-3 min-h-[52px] rounded-lg border text-left ${
                    current ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                  } disabled:opacity-60`}
                >
                  <span className="text-lg">{cfg.icon}</span>
                  <span className="flex-1">
                    <span className="block text-sm font-medium text-gray-900">{cfg.label}</span>
                    <span className="block text-xs text-gray-500">{cfg.description}</span>
                  </span>
                  {!allowed && <Lock size={14} className="text-gray-400" />}
                  {current && <Check size={16} className="text-blue-600" />}
                </button>
              );
            })}
          </div>
        )}

        {/* MATCH */}
        {view === 'match' && (
          <div className="p-4 space-y-3">
            <p className="text-sm text-gray-600">
              Tap the Before this After belongs to. If that Before already has an After, the old one is unmatched.
            </p>
            <div className="grid grid-cols-3 gap-2">
              {befores.map(b => {
                const selected = photo.pairedWithPhotoId === b.id;
                const takenByOther = photos.some(p => p.type === 'After' && p.id !== photo.id && p.pairedWithPhotoId === b.id);
                return (
                  <button
                    key={b.id}
                    disabled={busy || selected}
                    onClick={() => saveAndReturn({ pairedWithPhotoId: b.id }, 'Failed to match photo')}
                    className={`relative aspect-square rounded-lg overflow-hidden border-2 ${
                      selected ? 'border-green-500 ring-2 ring-green-200' : 'border-gray-200'
                    }`}
                  >
                    <img src={b.thumbnailUrl || b.url} alt={b.caption || 'Before'} className="w-full h-full object-cover" style={{ objectPosition: 'center 20%' }} />
                    {selected && (
                      <div className="absolute inset-0 bg-green-500/20 flex items-center justify-center">
                        <Check size={24} className="text-green-600" />
                      </div>
                    )}
                    {takenByOther && !selected && (
                      <div className="absolute top-0 left-0 right-0 bg-gray-900/70 text-white text-[10px] py-0.5 text-center">Has an After</div>
                    )}
                    {b.caption && (
                      <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[10px] p-1 truncate">{b.caption}</div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PhotoActionSheet;